import React, { useEffect, useState } from "react";

import SvgCheckSquare from "@uui/CheckSquare";
import SvgCopy01 from "@uui/Copy01";

import { Typography, SxProps, Stack } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { Variant } from "@mui/material/styles/createTypography";
import { TypographyPropsVariantOverrides } from "@mui/material/Typography/Typography";
import { OverridableStringUnion } from "@mui/types";

import CopiedTooltip from "@app/analytics/components/CopyToClipboard/CopiedTooltip";
import * as Events from "@app/analytics/tracking/events";
import log from "@app/utils/logger";

export interface CopyToClipboardProps {
  children?: React.ReactNode;
  onClick?: () => void;
  copyContent?: string;
  name?: string;
  sx?: SxProps<Theme>;
  stopPropagation?: boolean;
  className?: React.ComponentProps<any>["className"];
  variant?:
    | OverridableStringUnion<
        Variant | "inherit",
        TypographyPropsVariantOverrides
      >
    | "icon";
  tooltipOnCopy?: boolean;
}

/**
 * CopyToClipboard
 * Notes:
 * - `variant="icon"` will render a "copy-to-clipboard" icon that changes to a checkmark on success (note: must supply `copyContent`)
 * - by default, renders a <Typography> component and copies the children
 */
const CopyToClipboard = ({
  children,
  onClick,
  copyContent,
  sx,
  stopPropagation = false,
  name,
  variant = "bodySecondaryReg",
  className,
  tooltipOnCopy = true,
}: CopyToClipboardProps) => {
  const [isCopied, setCopied] = useState(false);

  const supportsCopy = navigator && navigator.clipboard;
  const sxSpan = { ...sx, cursor: supportsCopy ? "pointer" : "auto" };

  const handleClick = (e: React.MouseEvent<any>) => {
    if (stopPropagation) e.stopPropagation();

    if (name) {
      Events.copyToClipboard({ name });
    }

    if (onClick) {
      onClick();
    }

    if (supportsCopy && copyContent) {
      navigator.clipboard.writeText(copyContent);
      setCopied(true);
    } else if (supportsCopy) {
      navigator.clipboard.writeText(`${children}`);
      setCopied(true);
    } else {
      log.debug("CopyToClipboard: navigator.clipboard is undefined");
    }
  };

  useEffect(() => {
    if (!isCopied) return;
    setTimeout(() => setCopied(false), 2000);
  }, [isCopied]);

  const showTooltip = tooltipOnCopy && isCopied;

  return (
    <CopiedTooltip title="Copied!" open={showTooltip} placement="top">
      {variant === "icon" ? (
        <Stack
          sx={sxSpan}
          alignItems="center"
          onClick={handleClick}
          className={className}
        >
          {isCopied ? (
            <SvgCheckSquare
              sx={{ color: "shades.positive.500", fontSize: "20px" }}
            />
          ) : (
            <SvgCopy01
              sx={{
                fontSize: "20px",
                color: "shades.neutral.100",
                "&:hover": {
                  color: "shades.primary.500",
                },
              }}
            />
          )}
        </Stack>
      ) : (
        <Typography variant={variant} sx={sxSpan} onClick={handleClick}>
          {children}
        </Typography>
      )}
    </CopiedTooltip>
  );
};

export default CopyToClipboard;
